import React, { useState } from 'react';
import Typography from '@navi/web-ui/lib/primitives/Typography/index';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { LocalStorage } from '@cp/utils/StorageUtils';
import { Link } from 'react-router-dom';
import { persistQueryParamsRoutes } from './SideBarItems';
import { CosmosSyncBlockStatus } from '@cp/reducers/commonSlice';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { MORE_MENU_KEY, PARAMS_MAP_KEY } from './utils';
import styles from './index.module.scss';
import cx from 'classnames';
import MoreMenuItems from './MoreMenu';
import { clickStreamEvent } from '../NavigationLinks/interface';

interface ISidebarLinks {
  activeIcon: JSX.Element;
  inActiveIcon: JSX.Element;
  name: string;
  route: string;
  subRoute?: string;
  activeQueryParam?: string;
  linkClickHandler: (clickStreamEvent: clickStreamEvent) => void;
  currentPathname: string;
  currentSearch: string;
  clickStreamEvent: clickStreamEvent;
  isTabSelected?: boolean;
  component?: React.ReactNode;
  moreMenuItems?: GenericObject[];
}

export const clearParamsMap = () => {
  paramsMap = {};
  localStorage.removeItem(PARAMS_MAP_KEY);
};

let paramsMap: Record<string, string> = LocalStorage.getItem(PARAMS_MAP_KEY) || {};

const SidebarLinks: React.FC<ISidebarLinks> = props => {
  const {
    activeIcon,
    inActiveIcon,
    name,
    route,
    subRoute,
    activeQueryParam,
    linkClickHandler,
    currentPathname = '',
    currentSearch = '',
    clickStreamEvent,
    isTabSelected = false,
    component,
    moreMenuItems = []
  } = props;

  const isAgentOffline = useSelector((store: RootState) => !store.common.isAgentOnline);
  const isLonghornBlocked =
    useSelector((store: RootState) => store.common.cosmosSyncBlockStatus) ===
    CosmosSyncBlockStatus.BLOCKED;
  const isImpersonation = useSelector(
    (state: RootState) => state?.common?.userData?.userImpersonated
  );

  const isSidebarBlocked = (isAgentOffline || isLonghornBlocked) && !isImpersonation;

  const getActive = () => {
    if (currentPathname.includes(route) || isTabSelected) {
      return true;
    }
    if (subRoute && currentPathname.includes(subRoute)) {
      return true;
    }
    if (activeQueryParam && currentSearch.includes(activeQueryParam)) {
      return true;
    }
    return false;
  };
  const isActive = getActive();

  const [isHovered, setIsHovered] = useState(false);

  const isParamsDiff = paramsMap[currentPathname] !== currentSearch;

  if ((persistQueryParamsRoutes.includes(currentPathname) || isActive) && isParamsDiff) {
    paramsMap = LocalStorage.getItem(PARAMS_MAP_KEY) || {};
    paramsMap[currentPathname] = currentSearch;
    localStorage.setItem(PARAMS_MAP_KEY, JSON.stringify(paramsMap));
  }

  const iconSize = name === MORE_MENU_KEY || moreMenuItems?.length > 0 ? 16 : 24;
  const iconProps = { width: iconSize, height: iconSize };

  if (name === MORE_MENU_KEY) {
    const selectedMoreItem = moreMenuItems?.find(item => currentPathname.includes(item.route));

    return (
      <MoreMenuItems
        selectedMoreItem={selectedMoreItem}
        moreMenuItems={moreMenuItems || []}
        currentPathname={currentPathname}
        isSidebarBlocked={isSidebarBlocked}
        linkClickHandler={linkClickHandler}
        clickStreamEvent={clickStreamEvent}
      />
    );
  }

  return (
    <Link
      to={isSidebarBlocked ? '#' : `${route}${paramsMap[route] || ''}`}
      className={cx(styles.sideNavBarLinks, {
        '!cursor-not-allowed': isSidebarBlocked,
        active: isActive && !isSidebarBlocked
      })}
      onClick={() => {
        linkClickHandler(clickStreamEvent);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      id={route}
    >
      <>
        {typeof component !== 'undefined' ? component : null}
        <div className={styles.iconWrapper}>
          {isHovered && !isSidebarBlocked
            ? React.cloneElement(activeIcon, iconProps)
            : React.cloneElement(inActiveIcon, iconProps)}
        </div>
        <Typography
          variant="h3"
          className={cx([!isActive && styles.opaque, styles.text, styles.showText])}
        >
          {name}
        </Typography>
      </>
    </Link>
  );
};

export default SidebarLinks;
